import React, { useCallback, useEffect, useState } from 'react';
import { sb } from '../../supabase';
import { getAppOrigin } from '../../lib/authRedirects';

// Mint, copy and revoke the public link for one RFQ.
//
// This replaced a deliberately locked box whose note said a public link "needs
// its own table, and before that it needs identity matching on claim (M2)".
// Both conditions are now met: M2 shipped on 2026-09-18, and
// request_public_links landed in 20260918174609. The note is kept in the git
// history rather than here, because the reason it existed stopped being true.
//
// What makes this safe to post, and what the locked box was protecting against:
// an /f/<slug> invite link is a key to ONE FACTORY — pressing "respond" begins
// claiming that factory's identity, so posting one publicly hands the factory
// page to whoever finishes the form first. A token here is scoped to a REQUEST
// and grants nothing. It is read through get_public_rfq, which withholds the
// trader's name, the request id, contact details, budget and attachments.
//
// The per-channel buttons are not decoration: they append the utm_source that
// set_profile_utm writes onto the supplier's registration row (item 8), which
// is the only way the answer to "which channel brought them" ever exists.

const CHANNELS = [
  { key: 'fb',     ar: 'فيسبوك',  en: 'Facebook' },
  { key: 'wa',     ar: 'واتساب',  en: 'WhatsApp' },
  { key: 'tiktok', ar: 'تيك توك', en: 'TikTok' },
];

const linkFor = (token, source) => {
  const base = `${getAppOrigin()}/rfq/${token}/quote`;
  return source ? `${base}?utm_source=${source}&utm_medium=social` : base;
};

const stateOf = (row) => {
  if (row.revoked_at) return 'revoked';
  if (row.expires_at && new Date(row.expires_at) <= new Date()) return 'expired';
  return 'active';
};

// `offerable` is a prop — see DistributePanel for why a panel must not infer it.
export default function PublicLinkPanel({ requestId, offerable = false, isAr, flash }) {
  const notLinkable = isAr
    ? 'هذا الطلب ملغي — لا يمكن إنشاء رابط له'
    : 'This request is cancelled — no link can be created';

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const { data, error } = await sb
      .from('request_public_links')
      .select('id, token, created_at, expires_at, revoked_at')
      .eq('request_id', requestId)
      .order('created_at', { ascending: false });
    if (error) {
      // Security rule 4 applies to our own backend: a swallowed read is how an
      // admin concludes "no links exist" and mints a second one.
      console.error('[PublicLinkPanel] read failed:', error.code || '', error.message || error);
      setErr(isAr ? 'تعذّرت قراءة الروابط.' : 'Could not read the links.');
      return;
    }
    setErr('');
    setRows(data || []);
  }, [requestId, isAr]);

  useEffect(() => { load(); }, [load]);

  const mint = async () => {
    setBusy(true);
    const { error } = await sb.rpc('admin_create_request_public_link', {
      p_request_id: requestId,
      p_expires_at: null,
    });
    setBusy(false);
    if (error) {
      console.error('[PublicLinkPanel] mint failed:', error.code || '', error.message || error);
      // Named code -> the admin's language. See RoutePanel for the reasoning.
      const notOfferable = String(error.message || '').includes('REQUEST_NOT_OFFERABLE');
      flash?.(notOfferable
        ? (isAr
          ? 'هذا الطلب لم يعد يستقبل عروضاً — لا يمكن إنشاء رابط له.'
          : 'This request is no longer collecting offers — no link can be created.')
        : (isAr ? 'تعذّر إنشاء الرابط.' : 'Could not create the link.'));
      return;
    }
    flash?.(isAr ? 'أُنشئ الرابط.' : 'Link created.');
    load();
  };

  const revoke = async (token) => {
    // Revocation is not undoable and the link may already be posted publicly,
    // so it asks. Everything else on this panel is reversible; this is not.
    const ok = window.confirm(isAr
      ? 'إبطال هذا الرابط نهائيًا؟ من يفتحه بعدها يرى صفحة معطّلة، وإن كنت قد نشرته فلن يعمل لأحد.'
      : 'Revoke this link permanently? Anyone opening it afterwards sees a dead page, and if you have already posted it, it stops working for everyone.');
    if (!ok) return;

    setBusy(true);
    const { error } = await sb.rpc('admin_revoke_request_public_link', { p_token: token });
    setBusy(false);
    if (error) {
      console.error('[PublicLinkPanel] revoke failed:', error.code || '', error.message || error);
      flash?.(isAr ? 'تعذّر الإبطال.' : 'Could not revoke.');
      return;
    }
    flash?.(isAr ? 'أُبطل الرابط.' : 'Link revoked.');
    load();
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      flash?.(isAr ? `نُسخ رابط ${label}` : `Copied ${label} link`);
    } catch (clipErr) {
      // Clipboard is blocked on insecure origins and in some browsers. Say so
      // rather than flashing a success the admin will trust and paste nothing.
      console.error('[PublicLinkPanel] clipboard write failed:', clipErr?.message || clipErr);
      flash?.(isAr ? 'تعذّر النسخ — انسخ الرابط يدويًا.' : 'Copy failed — copy the link by hand.');
    }
  };

  const active = rows.filter((r) => stateOf(r) === 'active');

  return (
    <div className="ac-card" style={{ padding: 20 }}>
      <strong style={{ fontSize: 14 }}>
        {isAr ? 'رابط عام للنشر خارج المنصة' : 'Public link for sharing off-platform'}
      </strong>

      <p style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--ac-muted)', margin: '10px 0 14px' }}>
        {isAr
          ? 'يُقرأ بلا تسجيل، ولا يحمل اسم التاجر ولا بياناته ولا الميزانية ولا المرفقات. المورّد الذي يريد التقديم يسجّل حسابًا جديدًا — ولا يطالب بأي مصنع قائم. استعمل أزرار القنوات لينسب التسجيل إلى قناته.'
          : 'Readable without signing in, and carries no trader name, no contact details, no budget and no attachments. A supplier who wants to quote registers a new account — it never claims an existing factory. Use the channel buttons so a registration is attributed to where it came from.'}
      </p>

      {err && <p style={{ fontSize: 13, color: 'var(--ac-danger, #c0392b)' }}>{err}</p>}

      {rows.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ac-muted)', margin: '0 0 12px' }}>
          {isAr ? 'لا يوجد رابط لهذا الطلب بعد.' : 'No link for this request yet.'}
        </p>
      )}

      {rows.map((r) => {
        const st = stateOf(r);
        const dead = st !== 'active';
        return (
          <div
            key={r.id}
            style={{
              border: '1px solid var(--ac-line, rgba(0,0,0,.08))',
              borderRadius: 8,
              padding: 12,
              marginBottom: 10,
              opacity: dead ? 0.55 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12, wordBreak: 'break-all', flex: 1, minWidth: 200 }}>
                {linkFor(r.token, null)}
              </code>
              <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--ac-muted)' }}>
                {st === 'active' ? (isAr ? 'فعّال' : 'active')
                  : st === 'revoked' ? (isAr ? 'مُبطَل' : 'revoked')
                    : (isAr ? 'منتهٍ' : 'expired')}
              </span>
            </div>

            {!dead && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                <button className="ac-btn ac-btn-sm" onClick={() => copy(linkFor(r.token, null), isAr ? 'عادي' : 'plain')}>
                  {isAr ? 'نسخ' : 'Copy'}
                </button>
                {CHANNELS.map((c) => (
                  <button
                    key={c.key}
                    className="ac-btn ac-btn-sm"
                    onClick={() => copy(linkFor(r.token, c.key), isAr ? c.ar : c.en)}
                  >
                    {isAr ? c.ar : c.en}
                  </button>
                ))}
                <button className="ac-btn ac-btn-sm" onClick={() => revoke(r.token)} disabled={busy}>
                  {isAr ? 'إبطال' : 'Revoke'}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* DISABLED, NOT HIDDEN. This is the one path that would still have
          degraded gracefully -- get_public_rfq returns link_status 'closed', so
          the page tells whoever opens it -- but a link born dead wastes the
          person it is posted to, and a token in circulation cannot be taken
          back. admin_create_request_public_link refuses it either way. */}
      {!offerable && (
        <p style={{
          fontSize: 12, color: 'var(--ac-warn)', lineHeight: 1.7,
          background: 'var(--ac-warn-bg)', border: '1px solid var(--ac-warn-bd)',
          borderRadius: 8, padding: '8px 11px', margin: '0 0 10px',
        }}>
          {notLinkable}
        </p>
      )}
      <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={mint}
        disabled={busy || !offerable}
        style={!offerable ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        title={!offerable ? notLinkable : undefined}>
        {busy ? (isAr ? '…' : '…') : (isAr ? 'إنشاء رابط جديد' : 'Create a new link')}
      </button>

      {active.length > 1 && (
        <p style={{ fontSize: 12, color: 'var(--ac-muted)', margin: '10px 0 0' }}>
          {isAr
            ? `${active.length} روابط فعّالة لهذا الطلب — كلها تعمل. أبطِل ما لم تعد تنشره حتى تبقى النسبة للقناة صحيحة.`
            : `${active.length} active links on this request — all of them work. Revoke the ones you are no longer posting so channel attribution stays honest.`}
        </p>
      )}
    </div>
  );
}
